// src/components/AdminStarlinkDevice.jsx
//
// What clicking a Starlink on the site dashboard opens. Matches the
// Twenty4Seven mockup screens 1c (desktop) and 2c (mobile) — the client-facing
// Starlink terminal view: usage cycle as the hero, live telemetry, signal
// quality ring, approximate location. Admin can view any terminal regardless
// of site/client assignment. CSS reflows 1c -> 2c at the 768px breakpoint
// rather than branching the JSX, since the two mockups carry identical data,
// just re-stacked.

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiFetchJSON } from '../api/apiClient';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';
import { safeArr, fmt1, fmt2, RingGauge } from './monitoring/t247Shared';
import StarlinkBarChart from './StarlinkBarChart';
import '../styles/fleet-theme.css';
import './StarlinkDevicePage.css';
import './StarlinkQuota.css';
const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4'; // lives in public/, not src/images/ - needed for a stable, non-hashed URL the index.html preload tag can target

// Same fix already used in LeafletMap.js - default marker icons don't
// resolve correctly under webpack without this.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function formatUptime(seconds) {
  if (seconds == null) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

function formatShortDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
}

// Flag emoji from a 2-letter ISO country code - pure client-side, no
// backend/library needed (regional indicator Unicode trick).
function countryFlag(code) {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  const codePoints = [...upper].map((c) => 0x1f1e6 + (c.charCodeAt(0) - 65));
  return String.fromCodePoint(...codePoints);
}

// Country name from a 2-letter ISO code, via the browser's built-in
// Intl.DisplayNames - no library, no backend call.
function countryName(code) {
  if (!code) return null;
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase());
  } catch {
    return null;
  }
}

// Cycle-usage status tiers - not specified in the mockup beyond the one
// "WELL WITHIN PLAN" example, so these thresholds are a reasonable
// judgment call: < 50% reads as comfortably clear, 50-79% still fine but
// worth knowing, 80-99% is the same threshold as the alert marker on the
// progress bar, 100%+ is over-plan. Matches the brand's existing 3-tier
// chip convention (turquoise/neon/white) rather than inventing a new one.
// 1000 GB = 1 TB, matching how the billing query converts invoice text.
function fmtGB(gb) {
  if (gb == null) return '—';
  const n = Number(gb);
  if (n >= 1000) return `${+(n / 1000).toFixed(2)} TB`;
  return `${+n.toFixed(2)} GB`;
}

function getUsageStatus(percent) {
  if (percent >= 100) return { label: 'OVER PLAN', tier: 'critical' };
  if (percent >= 80) return { label: 'APPROACHING CAP', tier: 'warning' };
  if (percent >= 50) return { label: 'ON TRACK', tier: 'good' };
  return { label: 'WELL WITHIN PLAN', tier: 'good' };
}

export default function StarlinkDevicePage({ user, onLogout, scope = 'admin' }) {
  const { terminalId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [fleet, setFleet] = useState([]);

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const geocodeCache = useRef({});

  const [locationName, setLocationName] = useState(null);

  // In-memory only (per the request) - resets on refresh/navigation, no
  // backend storage. Polls the live-telemetry snapshot every 60s and
  // appends to these arrays, so the obstruction/latency/ping-drop charts
  // build up "since this page was opened" rather than showing a fixed
  // trailing window like the mockup's "last 12h" (which would need the
  // backend to actually retain and serve that history - it doesn't).
  const [obstructionHistory, setObstructionHistory] = useState([]);
  const [latencyHistory, setLatencyHistory] = useState([]);
  const TELEMETRY_POLL_MS = 60000;
  const [recheckingQuota, setRecheckingQuota] = useState(false);

  async function recheckQuota() {
    setRecheckingQuota(true);
    try {
      const json = await apiFetchJSON(`/api/v2/admin/starlink/${terminalId}?refresh_quota=true`);
      setData(json);
    } catch {
      // Keep the current numbers; the next poll will try again.
    } finally {
      setRecheckingQuota(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const json = await apiFetchJSON(`/api/v2/${scope}/starlink/${terminalId}`);
        if (!cancelled) {
          setData(json);
          const timeLabel = new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
          if (json?.live?.obstructionPercent != null) {
            setObstructionHistory([{ time: timeLabel, value: json.live.obstructionPercent }]);
          }
          if (json?.live?.latencyMs != null) {
            setLatencyHistory([{ time: timeLabel, latencyMs: json.live.latencyMs, pingDropPercent: json.live.pingDropPercent }]);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load device');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [terminalId, scope]);

  // Silent background poll - refreshes live telemetry without
  // re-triggering the loading spinner, and appends each reading to the
  // in-memory history arrays that feed the obstruction/latency charts.
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const json = await apiFetchJSON(`/api/v2/${scope}/starlink/${terminalId}`);
        setData(json);
        const now = new Date();
        const timeLabel = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
        if (json?.live?.obstructionPercent != null) {
          setObstructionHistory((prev) => [...prev, { time: timeLabel, value: json.live.obstructionPercent }]);
        }
        if (json?.live?.latencyMs != null) {
          setLatencyHistory((prev) => [...prev, { time: timeLabel, latencyMs: json.live.latencyMs, pingDropPercent: json.live.pingDropPercent }]);
        }
      } catch {
        // Silent poll - a transient failure just means one missed sample.
      }
    }, TELEMETRY_POLL_MS);
    return () => clearInterval(interval);
  }, [terminalId, scope]);

  // Fleet-wide list, so admin can jump between terminals directly from
  // this page - same pattern as the client version. Filtered to
  // non-ROAM only: ROAM terminals aren't under ELCOME management, so
  // there's no detail page data available for them anyway.
  useEffect(() => {
    let cancelled = false;

    async function loadFleet() {
      try {
        if (scope === 'client') {
          const json = await apiFetchJSON('/api/v2/client/dashboard?grouped=true');
          if (!cancelled) {
            const allDevices = (json.sites || []).flatMap((s) =>
              (s.devices || []).map((d) => ({ ...d, site_id: s.site_id, site_name: s.site_name }))
            );
            // Belt-and-suspenders ROAM exclusion: service !== 'roam' is the
            // primary signal, but ROAM entries also always come back with
            // data_available: false and no kit_serial - checking all three
            // is more robust against any one field being unset on older
            // migrated data.
            setFleet(allDevices.filter((d) =>
              d.device_type === 'starlink' &&
              d.service !== 'roam' &&
              d.data_available !== false &&
              !!d.kit_serial
            ));
          }
        } else {
          const json = await apiFetchJSON('/api/v2/admin/dashboard');
          if (!cancelled) {
            const siteDevices = (json.sites || []).flatMap((s) =>
              (s.devices || []).map((d) => ({ ...d, site_id: s.site_id, site_name: s.site_name }))
            );
            const unassignedDevices = json.unassigned?.starlink || [];
            const allDevices = [...siteDevices, ...unassignedDevices];
            setFleet(allDevices.filter((d) =>
              d.device_type === 'starlink' &&
              d.service !== 'roam' &&
              d.data_available !== false &&
              !!d.kit_serial
            ));
          }
        }
      } catch {
        // Device page should still work if fleet loading fails.
      }
    }

    loadFleet();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!data || !data.data_available || !data.location || !mapRef.current) return;

    if (mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
    }

    const { lat, lng } = data.location;
    const map = L.map(mapRef.current).setView([lat, lng], 6);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      className: 'asd-dark-tiles',
    }).addTo(map);

    L.marker([lat, lng]).addTo(map).bindPopup(data.kit || 'Starlink terminal');

    mapInstance.current = map;

    return () => {
      map.remove();
      mapInstance.current = null;
    };
  }, [data]);

  // Reverse-geocode the terminal's approximate location to a town/city
  // name, same Nominatim pattern (and same fair-use caching) already
  // established in AdminSitesMap.jsx.
  useEffect(() => {
    if (!data || !data.data_available || !data.location) {
      setLocationName(null);
      return;
    }
    let cancelled = false;
    const { lat, lng } = data.location;
    const cacheKey = `${lat},${lng}`;
    if (geocodeCache.current[cacheKey]) {
      setLocationName(geocodeCache.current[cacheKey]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=10`, {
          headers: { Accept: 'application/json' },
        });
        const json = await res.json();
        const addr = json?.address || {};
        const place = addr.city || addr.town || addr.village || addr.county || null;
        geocodeCache.current[cacheKey] = place;
        if (!cancelled) setLocationName(place);
      } catch {
        // Network hiccup - leave locationName null, falls back to just
        // the country name below rather than claiming anything wrong.
      }
    })();
    return () => { cancelled = true; };
  }, [data]);

  // ---- Guided tour ("?" in FleetHeader) ---------------------------------
  // Same tour for /admin/starlink and /starlink; FleetHeader's scope
  // switches the usage step between our quota view and the client plan.
  usePageTour('starlink', { data }, { ready: !loading && !error && !!data?.data_available });

  if (loading) {
    return (
        <div className="cm-loading-overlay">
          <div className="cm-spinner-container">
            <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
              <source src={loadingVideo} type="video/mp4" />
            </video>
            <p>Loading Starlink data…</p>
          </div>
        </div>
    );
  }

  if (error) {
    return (
      <div>
        <FleetHeader title="Starlink Device" user={user} onLogout={onLogout} scope={scope} />
        <div className="asd-page">
          {scope === 'admin' && (
            <button className="asd-back" onClick={() => navigate(-1)}>← Back</button>
          )}
          <div className="ft-toolbar-error" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!data.data_available) {
    return (
      <div>
        <FleetHeader title="Starlink Device" user={user} onLogout={onLogout} scope={scope} />
        <div className="asd-page">
          {scope === 'admin' && (
            <button className="asd-back" onClick={() => navigate(-1)}>← Back</button>
          )}
          <div className="asd-notice">
            {data.note || 'No data available for this terminal.'}
          </div>
        </div>
      </div>
    );
  }

  const { live = {}, plan, usage, alerts = [], location, recentOutages = [] } = data;

  // Admin dropdown: only kits sharing this device's service line - a
  // service line groups the kits that are meant to be swapped for each
  // other (e.g. spares), unlike the client view which reasonably shows
  // every terminal the client owns regardless of grouping.
  const dropdownFleet = scope === 'admin'
    ? fleet.filter((d) => d.service_line && d.service_line === data.serviceLine)
    : fleet;

  // Client: percent of what the client bought (null when the plan isn't
  // known - then we show usage without a limit rather than guess one).
  // Admin: percent of OUR subscription; the quota panel shows both.
  const hasLimit = usage?.limitGB != null;
  const usagePercent = hasLimit ? Math.min(100, usage.percent || 0) : null;
  const quota = scope === 'admin' ? data.quota : null;
  const dailyValues = safeArr(usage?.dailyUsage).map((d) => Number(d.gb) || 0);
  const dailyDates = safeArr(usage?.dailyUsage).map((d) => d.date);
  const peakGB = dailyValues.length ? Math.max(...dailyValues) : 0;

  const monthlyValues = safeArr(usage?.monthlyUsage).map((d) => Number(d.gb) || 0);
  const monthlyLabels = safeArr(usage?.monthlyUsage).map((d) => d.month || d.date);
  const monthlyPeakGB = monthlyValues.length ? Math.max(...monthlyValues) : 0;

  return (
    <div>
      <FleetHeader title={data.kit || 'Starlink Device'} user={user} onLogout={onLogout} scope={scope} />
      <div className="asd-page">

        {/* ── IDENTITY BAR ────────────────────────────────────────────────
            Relocated from the header (mockup 1c shows this inline with
            the header's own logo/nav, but this app's header is shared
            dropdown-nav across every page, so it has no room for a
            per-device breadcrumb - moved to its own bar directly below
            instead. Only shows real segments: site/client name is
            included when the API actually provides one, not fabricated. */}
        <div className="asd-identity-bar" data-tour="sl-identity">
          {scope === 'admin' && (
            <button className="asd-back" onClick={() => navigate(-1)}>← Back</button>
          )}
          <div className="asd-crumb">
            <span>STARLINK</span>
            {(data.site_name || data.client_name) && (
              <>
                <span>/</span>
                <span>{data.site_name || data.client_name}</span>
              </>
            )}
            <span>/</span>
            <span className="current">{data.kit}</span>
          </div>
          <div className="asd-topbar-right">
            <span className="asd-view-chip">{scope === 'client' ? 'CLIENT VIEW' : 'ADMIN VIEW'}</span>
            <span className={`t247-live-badge ${data.online ? '' : 'is-off'}`}>
              <span className="t247-dot" />
              {data.online ? `ONLINE${data.uptimeSeconds != null ? ' · ' + formatUptime(data.uptimeSeconds) : ''}` : 'OFFLINE'}
            </span>
            {dropdownFleet.length > 1 && (
              <select
                data-tour="sl-switch"
                className="asd-fleet-switch"
                value={terminalId}
                onChange={(e) => navigate(scope === 'client' ? `/starlink/${e.target.value}` : `/admin/starlink/${e.target.value}`)}
                aria-label="Switch Starlink terminal"
              >
                {dropdownFleet.map((d) => (
                  <option key={d.device_key} value={d.device_key}>{d.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {alerts.length > 0 && (
          <div className="asd-alerts-row" data-tour="sl-alerts">
            {alerts.map((a) => <span key={a} className="t247-chip chip-cell">{a}</span>)}
          </div>
        )}

        {/* ── USAGE HERO ──────────────────────────────────────────────── */}
        {plan && usage && (
          <div className="asd-hero">
            <div data-tour="sl-usage">
            {quota ? (
              <QuotaPanel
                quota={quota}
                plan={plan}
                onRecheck={recheckQuota}
                rechecking={recheckingQuota}
              />
            ) : (
              <>
                <div className="t247-eyebrow">{plan.name}{plan.status ? ` · ${plan.status}` : ''}</div>
                <div className="asd-hero-number">
                  <span className="big">{usage.usedGB}</span>
                  <span className="unit">
                    {hasLimit ? `GB of ${fmtGB(usage.limitGB)} used` : 'GB used this cycle'}
                  </span>
                  {hasLimit && (() => {
                    const status = getUsageStatus(usagePercent);
                    return (
                      <span className={`asd-usage-status ${status.tier}`}>
                        {fmt1(usagePercent)}% · {status.label}
                      </span>
                    );
                  })()}
                </div>
                {hasLimit && (
                  <div className="asd-progress-track">
                    <div className="asd-progress-fill" style={{ width: `${usagePercent}%` }} />
                    <div className="asd-progress-marker" style={{ left: '80%' }} />
                  </div>
                )}
              </>
            )}
            </div>

            <div
              className="t247-statbox-grid"
              data-tour="sl-forecast"
              style={{ gridTemplateColumns: `repeat(${hasLimit && !quota ? 4 : 3}, 1fr)` }}
            >
              <StatBox label="AVG / DAY" value={usage.avgPerDayGB} unit="GB" />
              <StatBox label="FORECAST AT CYCLE END" value={usage.projectedGB} unit="GB" />
              <StatBox label="CYCLE RESETS" value={formatShortDate(usage.cycleEndDate)} small />
              {hasLimit && !quota && (
                <StatBox label="UNUSED DATA" value={fmt2(Math.max(0, (usage.limitGB || 0) - (usage.usedGB || 0)))} unit="GB" />
              )}
            </div>

            {dailyValues.length > 0 && (
              <div className="asd-dailyusage" data-tour="sl-charts">
                <div className="t247-panel-header-row" style={{ padding: '14px 0 0', border: 'none' }}>
                  <span>DAILY USAGE · THIS CYCLE</span>
                  <span className="t247-muted-label">PEAK {fmt2(peakGB)} GB</span>
                </div>
                <div style={{ marginTop: 10 }}>
                  <StarlinkBarChart
                    data={safeArr(usage?.dailyUsage)}
                    labelKey="date"
                    valueKey="gb"
                    height={80}
                    unit="GB"
                  />
                </div>
                <div className="asd-chart-xlabels">
                  <span>{formatShortDate(dailyDates[0]).toUpperCase()}</span>
                  <span>TODAY</span>
                </div>
              </div>
            )}

            {monthlyValues.length > 0 && (
              <div className="asd-dailyusage">
                <div className="t247-panel-header-row" style={{ padding: '14px 0 0', border: 'none' }}>
                  <span>MONTHLY USAGE · LAST 12 MONTHS</span>
                  <span className="t247-muted-label">PEAK {fmt2(monthlyPeakGB)} GB</span>
                </div>
                <div style={{ marginTop: 10 }}>
                  <StarlinkBarChart
                    data={safeArr(usage?.monthlyUsage)}
                    labelKey="month"
                    valueKey="gb"
                    height={80}
                    unit="GB"
                  />
                </div>
                <div className="asd-chart-xlabels">
                  <span>{monthlyLabels[0] || ''}</span>
                  <span>{monthlyLabels[monthlyLabels.length - 1] || 'THIS MONTH'}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── LIVE TELEMETRY / TERMINAL & SERVICE / MAP ─────────────────── */}
        <div className="asd-triptych">
          <div className="t247-panel asd-col" data-tour="sl-telemetry">
            <div className="t247-panel-header-row"><span>LIVE TELEMETRY</span></div>
            <div className="asd-telemetry-grid">
              <TeleCell label="DOWNLINK" value={live.downlinkMbps?.toFixed(2) ?? '—'} unit="Mbps" />
              <TeleCell label="UPLINK" value={live.uplinkMbps?.toFixed(2) ?? '—'} unit="Mbps" />
              <TeleCell label="LATENCY" value={live.latencyMs ?? '—'} unit="ms" />
              <TeleCell label="PING DROP" value={live.pingDropPercent ?? '—'} unit="%" />
            </div>
            <div className="asd-signal-row">
              <RingGauge percent={live.signalPercent ?? 0} size={72} stroke={9} />
              <div>
                <div className="t247-eyebrow">SIGNAL QUALITY</div>
                <div className="asd-signal-value">{live.signalPercent ?? '—'}%</div>
                <div className="asd-signal-sub">Obstruction {live.obstructionPercent ?? '—'}%</div>
              </div>
            </div>

            {obstructionHistory.length > 0 && (
              <div className="asd-minichart-block">
                <div className="t247-muted-label">OBSTRUCTION · SINCE OPENED</div>
                <StarlinkBarChart
                  data={obstructionHistory}
                  labelKey="time"
                  valueKey="value"
                  height={54}
                  unit="%"
                />
              </div>
            )}

            {latencyHistory.length > 0 && (
              <div className="asd-minichart-block">
                <div className="t247-muted-label">LATENCY · SINCE OPENED</div>
                <StarlinkBarChart
                  data={latencyHistory}
                  labelKey="time"
                  valueKey="latencyMs"
                  height={54}
                  unit="ms"
                />
              </div>
            )}

            {latencyHistory.length > 0 && (
              <div className="asd-minichart-block">
                <div className="t247-muted-label">PING DROP · SINCE OPENED</div>
                <StarlinkBarChart
                  data={latencyHistory}
                  labelKey="time"
                  valueKey="pingDropPercent"
                  height={54}
                  unit="%"
                />
              </div>
            )}

            <div className="asd-live-note">
              {obstructionHistory.length > 0 || latencyHistory.length > 0
                ? `Tracking since this page opened, updated every ${TELEMETRY_POLL_MS / 1000}s.`
                : "Snapshot from this page load. Historical charting isn't wired in yet."}
            </div>
          </div>

          <div className="t247-panel asd-col" data-tour="sl-terminal">
            <div className="t247-panel-header-row"><span>TERMINAL</span></div>
            <InfoRow label="TERMINAL ID" value={data.device_key} mono />
            <InfoRow label="KIT SERIAL" value={data.kit} />
            <InfoRow label="DISH SERIAL" value={data.dish || '—'} />
            <InfoRow label="SOFTWARE VERSION" value={data.software || '—'} />
            <InfoRow label="PUBLIC IP" value={data.publicIp || '—'} mono />
            <div className="t247-panel-header-row" style={{ marginTop: 4 }}><span>SERVICE</span></div>
            {quota ? (
              <>
                <InfoRow label="OUR SUBSCRIPTION" value={plan?.usageLimitGB ? `${fmtGB(plan.usageLimitGB)} · ${plan.status || ''}` : '—'} />
                <InfoRow
                  label="CLIENT PLAN"
                  value={quota.clientGB != null ? fmtGB(quota.clientGB) : (quota.linked ? 'Not found this month' : 'Billing not linked')}
                />
              </>
            ) : (
              <InfoRow label="SERVICE PLAN" value={plan?.name || '—'} />
            )}
            <InfoRow label="OUTAGES · 30D" value="—" />
            <InfoRow label="SERVICE ACTIVATED" value="—" last={recentOutages.length === 0} />

            {recentOutages.length > 0 && (
              <>
                <div className="t247-panel-header-row" style={{ marginTop: 4 }}><span>RECENT OUTAGES</span></div>
                {recentOutages.map((o, i) => (
                  <div className="asd-outage-row" key={i}>
                    <span className="asd-outage-date">{formatShortDate(o.date).toUpperCase()}</span>
                    <div>
                      <div className="asd-outage-title">{o.reason} — {o.durationMin} min</div>
                      <div className="asd-outage-sub">{o.detail}</div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="t247-panel asd-col" data-tour="sl-location">
            <div className="t247-panel-header-row"><span>APPROXIMATE LOCATION</span></div>
            {location ? (
              <>
                <div className="asd-place-row">
                  {countryFlag(data.country)}{' '}
                  {locationName ? `${locationName}, ` : ''}
                  {countryName(data.country) || data.country || 'Unknown location'}
                </div>
                <div ref={mapRef} className="asd-map" />
                <div className="asd-location-note">
                  Decoded from the terminal's reported cell (~10km accuracy) - no
                  exact GPS coordinate is exposed by the API.
                </div>
              </>
            ) : (
              <div className="t247-empty">No location data for this terminal.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ADMIN QUOTA PANEL ─────────────────────────────────────────────────
// What we subscribe upstream vs what the client bought vs actual usage,
// on one bar, so the team can see when to top up before the client
// notices a throttle. Admin-only - the client API never carries these.
const QUOTA_LEVELS = {
  exhausted:    { tier: 'critical', label: 'SUBSCRIPTION USED UP', note: 'Usage has passed what we subscribe. The client is likely being throttled - top up now.' },
  topup_needed: { tier: 'critical', label: 'TOP UP NEEDED',        note: 'Usage has reached the top-up threshold of our subscription, and the client bought more than we subscribe.' },
  watch:        { tier: 'warning',  label: 'FORECAST OVER SUBSCRIPTION', note: 'Not urgent yet, but at the current daily average usage will pass our subscription before the cycle resets.' },
  ok:           { tier: 'good',     label: 'WITHIN SUBSCRIPTION',  note: 'Forecast stays within what we subscribe this cycle.' },
  covered:      { tier: 'good',     label: 'FULLY COVERED',        note: 'Our subscription is at least what the client bought. Nothing to manage.' },
  no_plan:      { tier: 'warning',  label: 'NO PAID PLAN THIS MONTH', note: 'This yacht is linked, but no paid Starlink Maritime invoice matched this month. The client sees usage without a plan limit.' },
  unlinked:     { tier: 'warning',  label: 'BILLING NOT LINKED',   note: "This site isn't linked to a billing yacht, so the client sees usage without a plan limit. Link the yacht in Site Manager." },
  unavailable:  { tier: 'warning',  label: 'BILLING LOOKUP FAILED', note: 'Could not read the client plan from billing. Re-check, or confirm the Supabase settings on the server.' },
  unknown:      { tier: 'warning',  label: 'NO SUBSCRIPTION DATA', note: 'The upstream plan limit is missing for this terminal.' },
};

function QuotaPanel({ quota, plan, onRecheck, rechecking }) {
  const level = QUOTA_LEVELS[quota.level] || QUOTA_LEVELS.unknown;
  const sub = quota.subscribedGB || 0;
  const client = quota.clientGB || 0;
  const used = quota.usedGB || 0;
  const projected = quota.projectedGB || used;
  const scale = Math.max(sub, client, used, projected, 1);
  const pct = (gb) => Math.min(100, (gb / scale) * 100);
  const pos = (gb) => `${pct(gb)}%`;
  // Anchor labels so ones near either edge stay inside the card.
  const labelAt = (gb) => {
    const p = pct(gb);
    return { left: `${p}%`, transform: `translateX(${p < 12 ? '0' : p > 88 ? '-100%' : '-50%'})` };
  };
  const c = quota.client;

  return (
    <div className="asd-quota">
      <div className="asd-quota-head">
        <div className="t247-eyebrow">
          {plan?.status || 'ACTIVE'}{quota.siteName ? ` · ${quota.siteName}` : ''}
        </div>
        <span className={`asd-usage-status ${level.tier}`}>{level.label}</span>
      </div>

      <div className="asd-hero-number">
        <span className="big">{fmt2(used)}</span>
        <span className="unit">
          GB used · {fmtGB(sub)} subscribed{client ? ` · ${fmtGB(client)} sold` : ''}
        </span>
      </div>

      <p className={`asd-quota-note ${level.tier}`}>{level.note}</p>

      <div className="asd-quota-bar" role="img"
        aria-label={`${fmt2(used)} GB used of ${fmtGB(sub)} subscribed${client ? ` and ${fmtGB(client)} sold to the client` : ''}`}>
        <div className="asd-progress-track asd-quota-track">
          {projected > used && (
            <div className="asd-quota-forecast" style={{ left: pos(used), width: `calc(${pos(projected)} - ${pos(used)})` }} />
          )}
          <div className={`asd-progress-fill asd-quota-fill ${level.tier}`} style={{ width: pos(used) }} />
          {sub > 0 && <div className="asd-quota-marker dashed" style={{ left: pos(quota.topupThresholdGB) }} title={`Top-up threshold: ${quota.topupThresholdPercent}% of our subscription`} />}
          {sub > 0 && <div className="asd-quota-marker strong" style={{ left: pos(sub) }} />}
          {client > 0 && <div className="asd-quota-marker" style={{ left: pos(client) }} />}
        </div>
        <div className="asd-quota-scale">
          {sub > 0 && <span className="strong" style={labelAt(sub)}>Ours {fmtGB(sub)}</span>}
          {client > 0 && Math.abs(client - sub) / scale > 0.15 && (
            <span style={labelAt(client)}>Client {fmtGB(client)}</span>
          )}
        </div>
      </div>

      <div className="t247-statbox-grid asd-quota-stats">
        <StatBox label="USED OF OUR SUBSCRIPTION" value={quota.percentOfSubscribed != null ? fmt1(quota.percentOfSubscribed) : '—'} unit="%" />
        <StatBox label="USED OF CLIENT PLAN" value={quota.percentOfClient != null ? fmt1(quota.percentOfClient) : '—'} unit={quota.percentOfClient != null ? '%' : ''} />
        <StatBox label="LEFT BEFORE THROTTLE" value={fmt2(Math.max(0, quota.headroomGB || 0))} unit="GB" />
        <StatBox
          label="SUBSCRIPTION RUNS OUT"
          value={used >= sub && sub > 0 ? 'Now' : (quota.subscriptionRunsOutOn ? formatShortDate(quota.subscriptionRunsOutOn) : 'Not this cycle')}
          small
        />
        <StatBox label="SUGGESTED TOP-UP" value={quota.projectedShortfallGB != null ? fmt2(quota.projectedShortfallGB) : '—'} unit={quota.projectedShortfallGB != null ? 'GB' : ''} />
        <StatBox label="MARGIN SOLD OVER SUB" value={quota.marginGB != null ? fmt2(quota.marginGB) : '—'} unit={quota.marginGB != null ? 'GB' : ''} />
      </div>

      <div className="asd-quota-source">
        {c && c.status && c.status !== 'not_configured' && c.status !== 'error' ? (
          <span>
            {c.plan_invoice
              ? <>Client plan <strong>{quota.clientPlanName || c.plan_name}</strong> from invoice {c.plan_invoice} ({c.plan_invoice_date}, {c.plan_source}).</>
              : 'No paid plan invoice matched this month.'}
            {c.booster_lines ? <> Boosters: {c.booster_lines}.</> : null}
            {c.stale ? ' Showing the last good billing read; the latest lookup failed.' : ''}
          </span>
        ) : (
          <span>{quota.linked ? 'Client plan unavailable.' : 'No billing yacht linked to this site.'}</span>
        )}
        {quota.lastAlert && (
          <span className="asd-quota-alerted">
            Team alerted {formatDate(quota.lastAlert.created_at.replace(' ', 'T') + 'Z')} at {fmtGB(quota.lastAlert.used_gb)} used.
          </span>
        )}
        {quota.linked && (
          <button className="asd-quota-recheck" onClick={onRecheck} disabled={rechecking}>
            {rechecking ? 'Checking invoices…' : 'Re-check invoices'}
          </button>
        )}
      </div>
    </div>
  );
}

const StatBox = ({ label, value, unit, small }) => (
  <div className="t247-statbox">
    <div className="label">{label}</div>
    <div className={`value ${small ? 'small' : ''}`}>
      {value} {unit && <span className="unit">{unit}</span>}
    </div>
  </div>
);

const TeleCell = ({ label, value, unit }) => (
  <div className="asd-telemetry-cell">
    <span>{label}</span>
    <strong>{value} <em>{unit}</em></strong>
  </div>
);

const InfoRow = ({ label, value, mono, last }) => (
  <div className={`asd-inforow ${last ? 'last' : ''}`}>
    <span className="k">{label}</span>
    <span className={`v ${mono ? 'mono' : ''}`}>{value}</span>
  </div>
);
